import { useEffect, useRef } from "react";

type Diff<T> = {
  [K in keyof T]?: {
    from: T[K] | undefined;
    to: T[K] | undefined;
  };
};

type LoggerOptions = {
  writeToFile?: boolean;
};

function getCallerLocation(): string {
  const err = new Error();
  if (!err.stack) return "unknown";

  const stackLines = err.stack.split("\n");
  const callerLine = stackLines[3];

  return callerLine ? callerLine.trim() : "unknown";
}

function downloadLogFile(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();

  URL.revokeObjectURL(url);
}

function getObjectDiff<T extends Record<string, any>>(
  prev: T,
  current: T,
): Diff<T> {
  const changes: Diff<T> = {};

  const allKeys = new Set([
    ...Object.keys(prev),
    ...Object.keys(current),
  ]) as Set<keyof T>;

  allKeys.forEach((key) => {
    if (prev[key] !== current[key]) {
      changes[key] = {
        from: prev[key],
        to: current[key],
      };
    }
  });

  return changes;
}

export function useStateLogger<T = any>(
  label?: string,
  value?: T,
  options: LoggerOptions = { writeToFile: false },
) {
  const { writeToFile = false } = options;

  const prevValueRef = useRef<any>(undefined);
  const renderCountRef = useRef<number>(0);
  const lastUpdateTimeRef = useRef<number>(Date.now());
  const redundantUpdateCountRef = useRef<number>(0);
  const fileBufferRef = useRef<string>("");
  const locationRef = useRef<string | null>(null);

  renderCountRef.current += 1;

  if (!locationRef.current) {
    locationRef.current = getCallerLocation();
  }

  const logInternal = (
    currentLabel: string,
    currentValue: any,
    source: "render" | "action",
  ) => {
    const now = Date.now();
    const prevValue = prevValueRef.current;
    const timeDiff = now - lastUpdateTimeRef.current;
    const changed = prevValue !== currentValue;

    if (!changed) {
      redundantUpdateCountRef.current += 1;
    }

    const payload = {
      label: currentLabel,
      source,
      location: locationRef.current,
      renderCount: renderCountRef.current,
      previousValue: prevValue,
      currentValue,
      changed,
      timeSinceLastChangeMs: timeDiff,
      redundantCount: redundantUpdateCountRef.current,
    };

    console.group(
      `State Debugger → ${payload.label} [${source}] @ ${payload.location}`,
    );

    console.log("Render Count:", payload.renderCount);
    console.log("Previous Value:", payload.previousValue);
    console.log("Current Value:", payload.currentValue);
    console.log("Changed:", payload.changed);
    console.log("Time Since Last Change (ms):", payload.timeSinceLastChangeMs);

    if (!changed && prevValue !== undefined) {
      console.warn("Redundant Update Detected:", {
        redundantCount: payload.redundantCount,
      });
    }

    if (
      changed &&
      prevValue &&
      currentValue &&
      typeof currentValue === "object" &&
      typeof prevValue === "object"
    ) {
      const diff = getObjectDiff(prevValue, currentValue);
      console.log("Differences:", diff);
    }

    console.groupEnd();

    if (writeToFile) {
      fileBufferRef.current +=
        JSON.stringify(
          { timestamp: new Date().toISOString(), ...payload },
          null,
          2,
        ) + "\n\n";

      downloadLogFile(
        `state-debug-${payload.label}.txt`,
        fileBufferRef.current,
      );
    }

    prevValueRef.current = currentValue;
    lastUpdateTimeRef.current = now;
  };

  // automatic render logging only if both provided
  useEffect(() => {
    if (label !== undefined && value !== undefined) {
      logInternal(label, value, "render");
    }
  }, [label, value]);

  // manual logger – fully independent
  const log = <V = any>(manualLabel: string, manualValue: V) => {
    logInternal(manualLabel, manualValue, "action");
  };

  return { log };
}
